<i18n>
{
  "en": {
    "createProductMasterTitle": "Creating product master",
    "editProductMasterTitle": "Editing product master",
    "name": "Product master name",
    "description": "Product master description",
    "referencePrice": "Reference price",
    "referencePriceHint": "Starting price for optimization process",
    "soldByWeight": "Sold by weight",
    "saleWeight": "Percentage of product",
    "saleWeightHint": "How much of the product is sold on each dish (1-1000)",
    "unitCost": "Cost price",
    "maxRangePrice": "Maximum price for master product",
    "minRangePrice": "Minimum price for master product",
    "processingSpeed": "Processing speed",
    "consumptionPerPerson": "Diners per plate",
    "image": "Select image from file",
    "emptyFieldError": "This field cannot be empty",
    "invalidNumberError": "Invalid number",
    "invalidPercentage":  "Invalid percentage",
    "fileSizeError": "Image is too big. Maximum size is 2 MB.",
    "fileFormatError": "This format is not valid",
    "features": "Product allergens",
    "previousFile": "Previous File",
    "productSearchHint": "If the product does not appear, please contact operations"

  },
  "es": {
    "createProductMasterTitle": "Creando producto maestro",
    "editProductMasterTitle": "Editando producto maestro",
    "name": "Nombre del producto maestro",
    "description":  "Descripción del producto maestro",
    "referencePrice":  "Precio de referencia",
    "referencePriceHint": "El precio que utilizará el optimizador de precios como punto de inicio en los cálculos",
    "soldByWeight": "Vendido al peso",
    "saleWeight": "Porcentaje del producto",
    "saleWeightHint": "Qué porcentaje del producto se vende en cada plato (1-1000)",
    "unitCost":  "Coste unitario",
    "maxRangePrice":  "Precio máximo del producto maestro",
    "minRangePrice":  "Precio mínimo del producto maestro",
    "processingSpeed":  "Velocidad de elaboración",
    "consumptionPerPerson":  "Comensales por plato",
    "image": "Seleccione la imagen desde el explorador",
    "emptyFieldError": "Este campo no puede estar vacio",
    "invalidNumberError": "Número no válido",
    "invalidPercentage":  "Porcentaje inválido",
    "fileSizeError": "La imagen es demasiado grande. El tamaño máximo es 2 MB.",
    "fileFormatError": "Este formato no es válido",
    "features":  "Alérgenos del producto",
    "previousFile": "Imagen anterior",
    "productSearchHint": "Si no aparece el producto contacte con operaciones"
  }
}
</i18n>

<template>
  <v-dialog v-model="visible" max-width="600px" persistent>
    <v-card v-if="visible" scrollable persistent class="px-7 py-4">
      <v-card-title class="pa-0 mb-2">
        {{ modalTitle }}
      </v-card-title>
      <v-sheet style="background-color: #ffffff">
        <v-form v-model="valid" ref="form">
          <v-text-field v-model="name" :rules="textFieldsRules" :label="$t('name')" class="required" required/>
          <v-text-field v-model="description" :rules="textFieldsRules" :label="$t('description')" class="required" required/>
          <v-text-field v-model="referencePrice" :rules="numberRules" :label="$t('referencePrice')" class="required" :hint="$t('referencePriceHint')" persistent-hint required/>
          <v-switch v-model="soldByWeight" dense :label="$t('soldByWeight')" class="pt-2 mt-0 master-product-input"/>
          <v-text-field v-if="soldByWeight" v-model="saleWeightIndex" :rules="percentageRules" :label="$t('saleWeight')" :hint="$t('saleWeightHint')" suffix="%" persistent-hint required/>
          <v-text-field v-model="unitCost" :rules="numberRules" :label="$t('unitCost')" class="required" required/>
          <v-text-field v-model="maxRangePrice" :rules="numberRules" :label="$t('maxRangePrice')" class="required" required/>
          <v-text-field v-model="minRangePrice" :rules="numberRules" :label="$t('minRangePrice')" class="required" required/>
          <v-text-field v-model="processingSpeed" :rules="numberRules" :label="$t('processingSpeed')" class="required" required/>
          <v-text-field v-model="consumptionPerPerson" :rules="numberRules" :label="$t('consumptionPerPerson')" class="required" required/>
          <!-- Image selector -->
          <ProductMasterFieldLanguages
            v-model="fieldsLanguages"
          />
          <v-select v-model="features" :items="featureList" :menu-props="{ maxHeight: '400' }"
          :label="$t('features')" item-value="guid" item-text="legendName" multiple chips/>
          <v-file-input v-model="image" :rules="imageRules" :show-size="showImageSize"
                        accept="image/png, image/jpeg, image/bmp" prepend-inner-icon="mdi-camera"
                        prepend-icon="" :label="$t('image')" class="mb-6"/>
          <v-autocomplete
            v-model="productsRelated"
            :items="productListWithSelected"
            :loading="isLoading"
            :search-input.sync="search"
            color="red"
            item-text="name"
            item-value="API"
            label="Products to select"
            placeholder="Start typing to search products"
            prepend-icon="mdi-database-search"
            return-object
            chips
            deletable-chips
            multiple
            hide-no-data
            outlined
            small-chips
            :hint="$t('productSearchHint')"
            persistent-hint
          >
            <template v-slot:item="data">
              <template>
                <v-list-item-avatar>
                  <v-img :src="data.item.image" :alt="data.item.name"
                         :lazy-src="defaultImg" contain/>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title v-html="data.item.name"></v-list-item-title>
                  <v-list-item-subtitle v-html="posNameByGuid[data.item.point_of_sale_guid]"></v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </template>
          </v-autocomplete>
          <dynameat-modal-buttons class="mt-3" @cancel="onCancel" @confirm="onConfirm" :confirmDisabled="!valid"/>
        </v-form>
      </v-sheet>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  imageFileType, maxSize, notEmptyString, notValidNumber, notValidPercentage
} from '@core/helpers/formValidators';
import Component from 'vue-class-component';
import { Components } from '@core/helpers/decorators';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import { ProductMasterModelLanguage } from '@productMaster/models/productMasterModelLanguage';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import ProductMasterFieldLanguages from './productMasterFieldLanguages.vue';
import { ProductMaster } from '../models';

@Component
@Components({ DynameatModalButtons, ProductMasterFieldLanguages })
export default class ProductMasterCreateEditModal extends Vue {
  @Prop({
    Object,
    validator(productMaster) {
      return productMaster instanceof ProductMaster;
    }
  }) productMaster;

  @Prop({
    String,
    default: 'create',
    validator(type) {
      return type === 'create' || type === 'edit';
    }
  }) type;

  @Prop({ Boolean, default: false }) visible;

  @Prop({ Array, default: [] }) featureList;

  @Prop({ Array, default: [] }) productList;

  @Prop({ Array, default: [] }) pointOfSaleList;

  @Prop({ Function, default: () => {} }) productSearchRequest;

  @Prop({ Number, default: 0.0 }) productSearchProgress;

  search = null;

  isLoading = false;

  valid = false;

  name = ''

  description = ''

  referencePrice = 0.0

  soldByWeight = false

  unitCost = 0.0

  maxRangePrice = 0.0

  minRangePrice = 0.0

  processingSpeed = 0

  consumptionPerPerson = 0

  saleWeightIndex = 0

  fieldsLanguages = {}

  image = null

  features = []

  productsRelated = []

  get defaultImg() {
    // eslint-disable-next-line global-require
    return require('@dynameat/assets/images/dynameat-logo-secondary.png');
  }

  get posArray() {
    return this.pointOfSaleList.map((pos) => pos.guid);
  }

  get posNameByGuid() {
    const pos = {};
    this.pointOfSaleList.forEach((pointOfSale) => {
      pos[pointOfSale.guid] = pointOfSale.name;
    });
    return pos;
  }

  get productsRelatedFiltered() {
    return this.productsRelated.filter((product) => this.posArray.includes(product.point_of_sale_guid));
  }

  get productListWithSelected() {
    return [...this.productList, ...this.productsRelatedFiltered];
  }

  get modalTitle() {
    return this.$t(`${this.type}ProductMasterTitle`);
  }

  get textFieldsRules() {
    return [notEmptyString(this.$t('emptyFieldError'))];
  }

  get numberRules() {
    return [notValidNumber(this.$t('invalidNumberError'))];
  }

  get percentageRules() {
    return [notValidPercentage(this.$t('invalidPercentage'), 1, 1000)];
  }

  get imageRules() {
    return [maxSize(this.$t('fileSizeError'), 2000000), imageFileType(this.$t('fileFormatError'))];
  }

  get showImageSize() {
    return this.image instanceof File && this.image.size > 0;
  }

  onCancel() {
    this.$emit('cancel');
    this.clearOwnData();
  }

  onConfirm() {
    if (this.$refs.form.validate()) {
      const modelLanguageList = [];
      this.productMaster.name = this.name;
      this.productMaster.description = this.description;
      this.productMaster.referencePrice = this.referencePrice;
      this.productMaster.soldByWeight = this.soldByWeight;
      this.productMaster.unitCost = this.unitCost;
      this.productMaster.maxRangePrice = this.maxRangePrice;
      this.productMaster.minRangePrice = this.minRangePrice;
      this.productMaster.processingSpeed = this.processingSpeed;
      this.productMaster.consumptionPerPerson = this.consumptionPerPerson;
      this.productMaster.image = this.image;
      this.productMaster.saleWeightIndex = this.saleWeightIndex;
      this.productMaster.features = this.features;
      this.productMaster.products = this.productsRelated;
      Object.keys(this.fieldsLanguages).forEach((key) => {
        modelLanguageList.push(this.fieldsLanguages[key]);
      });
      this.productMaster.modelLanguage = ProductMasterModelLanguage.toInstanceList(modelLanguageList.map((each) => ({
        model_language: each.modelLanguage,
        commercial_name: each.commercialName,
        commercial_description: each.commercialDescription,
        long_commercial_description: each.longCommercialDescription,
      })));
      this.$emit('confirm', this.productMaster);
    }
  }

  clearOwnData() {
    this.name = '';
    this.description = '';
    this.referencePrice = 0.0;
    this.soldByWeight = false;
    this.unitCost = 0.0;
    this.maxRangePrice = 0.0;
    this.minRangePrice = 0.0;
    this.processingSpeed = 0;
    this.consumptionPerPerson = 0;
    this.saleWeightIndex = 0;
    this.commercialName = {};
    this.commercialDescription = {};
    this.longCommercialDescription = {};
    this.image = null;
    this.features = [];
    this.fieldsLanguages = {};
  }

  @Watch('productMaster', { immediate: true })
  onRecommendationChanged(updatedProductMaster) {
    if (updatedProductMaster) {
      this.clearOwnData();
      this.name = updatedProductMaster.name;
      this.description = updatedProductMaster.description;
      this.referencePrice = String(updatedProductMaster.referencePrice);
      this.soldByWeight = updatedProductMaster.soldByWeight;
      this.unitCost = String(updatedProductMaster.unitCost);
      this.maxRangePrice = String(updatedProductMaster.maxRangePrice);
      this.minRangePrice = String(updatedProductMaster.minRangePrice);
      this.processingSpeed = String(updatedProductMaster.processingSpeed);
      this.consumptionPerPerson = String(updatedProductMaster.consumptionPerPerson);
      this.saleWeightIndex = updatedProductMaster.saleWeightIndex;
      updatedProductMaster.modelLanguage.forEach((each) => {
        this.fieldsLanguages[each.modelLanguage] = {
          commercialName: each.commercialName,
          commercialDescription: each.commercialDescription,
          longCommercialDescription: each.longCommercialDescription,
          modelLanguage: each.modelLanguage
        };
      });
      this.productsRelated = updatedProductMaster.products;
      this.image = updatedProductMaster.image ? new File([], this.$t('previousFile')) : null;
      this.features = updatedProductMaster.features;
    }
  }

  @Watch('search')
  async onSearch(searchTerm) {
    if (this.isLoading) return;

    this.isLoading = true;

    try {
      await this.productSearchRequest({ search: searchTerm, product_master: true });
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('productListError')));
    } finally {
      this.isLoading = false;
    }
  }
}
</script>

<style scoped>
</style>
